/**
 * @genesis_domain Shared Schemas — Ingestion Contracts
 * @genesis_version V9.1
 * @genesis_intent Ingestion-specific Zod schemas for Schema.org JSON-LD parsing and universal adapter output.
 * @genesis_boundaries FORBIDDEN from containing request or response schemas. Pure ingestion format contracts only.
 * @genesis_ai_trail Sharded from api.ts during v4.1 Cartography Cleanup (Strike 4).
 */

import { z } from 'zod';

/**
 * Schema.org/Recipe compatible JSON import schema.
 * Supports both the full Schema.org structured data format and our own
 * RecipeSchema-compatible JSON format. Used by JsonAdapter for deterministic
 * JSON imports (no AI invocation).
 */
export const SchemaOrgRecipeSchema = z.object({
    '@context': z.string().url().optional(),
    '@type': z.literal('Recipe').optional(),
    // ── Three Culinary Pillars (Strict) ──────────────────────────────────────
    name: z.string().min(1, 'Recipe name is required'),
    recipeIngredient: z.array(z.string()).optional(),
    recipeInstructions: z.array(z.union([z.string(), z.object({ text: z.string() })])).optional(),
    // ── Soft-Consumed Fields (Flexible) ──────────────────────────────────────
    description: z.string().optional().nullable(),
    prepTime: z.string().optional().nullable(),
    cookTime: z.string().optional().nullable(),
    totalTime: z.string().optional().nullable(),
    recipeYield: z.union([z.string(), z.number(), z.array(z.unknown())]).optional().nullable(),
    // ── Unconsumed / Volatile Metadata (Permissive) ──────────────────────────
    author: z.unknown().optional().nullable(),
    keywords: z.unknown().optional().nullable(),
    image: z.unknown().optional().nullable(),
    url: z.unknown().optional().nullable(),
    recipeCuisine: z.unknown().optional().nullable(),
    recipeCategory: z.unknown().optional().nullable(),
    suitableForDiet: z.unknown().optional().nullable(),
    aggregateRating: z.unknown().optional().nullable(),
    video: z.unknown().optional().nullable(),
    review: z.unknown().optional().nullable(),
    datePublished: z.unknown().optional().nullable(),
    dateModified: z.unknown().optional().nullable(),
    isPartOf: z.unknown().optional().nullable(),
    mainEntityOfPage: z.unknown().optional().nullable(),
    nutrition: z.record(z.string(), z.unknown()).optional().nullable(),
}).passthrough();

export type SchemaOrgRecipe = z.infer<typeof SchemaOrgRecipeSchema>;

/**
 * Normalized ingestion result after parsing by any adapter.
 * This is the universal output format of the IngestionEngine.
 */
export const IngestionResultSchema = z.object({
    id: z.string().uuid().optional(),
    title: z.string(),
    description: z.string().optional(),
    instructions: z.string().optional(),
    prepTimeMinutes: z.number().int().optional(),
    cookTimeMinutes: z.number().int().optional(),
    servings: z.number().int().optional(),
    /** Fast-Path Temperature Sweeper: extracted from instructions text when schema.org lacks cookTemperature */
    rawTemperature: z.number().nullable().optional(),
    rawTemperatureUnit: z.string().nullable().optional(),
    temperatureContext: z.string().nullable().optional(),
    sourceUrl: z.string().url().optional(),
    imageUrl: z.string().url().optional(),
    ingredients: z.array(
        z.object({
            name: z.string(),
            quantityDisplay: z.string().nullable().optional(),
            unitText: z.string().nullable().optional(),
        })
    ),
    source: z.enum(['json', 'csv', 'xlsx', 'pdf', 'docx', 'ai']),
    aiMetadata: z
        .object({
            provider: z.string(),
            model: z.string(),
            extractionMethod: z.string(),
        })
        .optional(),
    categories: z.array(z.string()).optional(),
    cuisine: z.string().optional(),
    sourceName: z.string().optional(),
    sourceAuthor: z.string().optional(),
    notes: z.string().nullable().optional(),
    rating: z.number().int().min(1).max(5).nullable().optional(),
    sourceRating: z.number().nullable().optional(),
    sourceReviewCount: z.number().int().min(0).nullable().optional(),
    nutrition: z.record(z.string(), z.unknown()).optional(),
    /** Two-Pass Parser: structured category details from HRV internal exports for UUID-based resolution */
    hrvCategoryDetails: z.array(z.object({
        id: z.string(),
        name: z.string(),
        type: z.string().optional(),
        source: z.enum(['user', 'ai', 'web']).optional(),
    })).optional(),
}).passthrough();

export type IngestionResult = z.infer<typeof IngestionResultSchema>;